import { FormHandles } from '@unform/core';
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { AuthLayout } from '../../../../components/AuthLayout';
import { AuthFormHeader } from '../../../../components/AuthFormHeader';
import { Button } from '../../../../components/Button';
import { Icon } from '../../../../components/Icon';
import { InputText } from '../../../../components/InputText';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { getValidationErrors } from '../../../../utils/getValidationErrors';

import { BackLink, Form } from './styles';

interface formProps {
  email: string;
}

const ForgotPasswordStep1: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (data: formProps) => {
      try {
        formRef.current?.setErrors({});
        setLoading(true);

        const schema = yup.object().shape({
          email: yup
            .string()
            .email('Insira um email válido.')
            .required('Campo obrigatório.'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.post('users/password/forgot', {
          email: data.email,
        });

        addToast({
          type: 'info',
          title: 'Token enviado',
          description: 'Verifique no seu email e na caixa de spam.',
        });

        setLoading(false);
        navigate('/forgot-password/verify', { state: { email: data.email } });
      } catch (err: any) {
        setLoading(false);

        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.status === 404) {
          addToast({
            type: 'error',
            title: 'Email não cadastrado',
            description:
              'Esse email não está cadastrado na nossa plataforma. Entre em contato com a sua instituição para realizar o cadastro.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na requisição',
          description:
            'Houve um erro na requisição realizada. Tente novamente mais tarde.',
        });
      }
    },
    [addToast, navigate],
  );

  return (
    <AuthLayout
      eyebrow="Protocolo de segurança"
      title={
        <>
          RECUPERE <span>SEU ACESSO</span>
        </>
      }
      subtitle="Insira o email cadastrado para receber um código de verificação de 5 dígitos e voltar a acessar sua conta."
    >
      <AuthFormHeader
        badge="Acesso seguro"
        title="Esqueceu sua senha?"
        subtitle="Insira o email cadastrado. Um token de verificação será enviado para ele."
      />

      <Form ref={formRef} onSubmit={handleSubmit}>
        <InputText
          label="Email cadastrado"
          name="email"
          placeholder="exemplo@dominio.com"
        />

        <Button
          name="submit"
          value={loading ? 'Enviando...' : 'Enviar código'}
          disabled={loading}
        />
      </Form>

      <BackLink type="button" onClick={() => navigate('/sign-in')}>
        <Icon name="arrow_back" size={18} color={colors.onSurfaceVariant} />
        Voltar ao login
      </BackLink>
    </AuthLayout>
  );
};

export { ForgotPasswordStep1 };
